import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, AlertCircle, Check, Copy, ExternalLink, FileText, Printer,
} from 'lucide-react';
import { careEnquiryService } from '../../../services';
import { Button, Modal } from '../../../components';
import {
  wording, StatusBadge, Journey, formatDate, formatDateTime, initials, errorMessage,
  inputCls, labelCls, panelCls, statusLabel,
} from './enquiryShared';

/**
 * Panel / Line / Note live at MODULE level, not inside EnquiryDetail.
 *
 * A component declared inside another is a NEW function on every render, so
 * React treats it as a different component type, unmounts the old tree and
 * mounts a fresh one. Every input inside it is destroyed and recreated — which
 * loses focus after a single keystroke, since typing sets state and re-renders.
 * Keeping their identity stable is what lets somebody type a whole email
 * address into the assessor form.
 */
const Panel = ({ kicker, title, description, children, footer }) => (
  <section className={panelCls}>
    <div className="p-6 border-b border-[#E3E9F3] dark:border-gray-800">
      {kicker && <div className="text-[9.5px] font-bold tracking-wider text-blue-600 mb-2">{kicker}</div>}
      <h2 className="text-[19px] font-bold text-[#1E293B] dark:text-gray-100 tracking-tight">{title}</h2>
      {description && <p className="text-[12.5px] text-[#7B8BA4] mt-1.5">{description}</p>}
    </div>
    <div className="p-6 space-y-4">{children}</div>
    {footer && (
      <div className="px-6 py-4 bg-[#FCFDFF] dark:bg-[#1f242b] border-t border-[#E3E9F3] dark:border-gray-800 flex flex-wrap justify-end gap-3">
        {footer}
      </div>
    )}
  </section>
);

const Line = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-3 border-b border-[#EDF1F7] dark:border-gray-800 text-[12.5px]">
    <span className="text-[#8C9BB0]">{label}</span>
    <strong className="font-semibold text-right text-[#334155] dark:text-gray-200 whitespace-pre-wrap">{children}</strong>
  </div>
);

const Note = ({ tone = 'blue', children }) => (
  <div className={`rounded-xl p-4 text-[12.5px] leading-relaxed border ${tone === 'amber'
    ? 'bg-amber-50 border-amber-100 text-amber-800 dark:bg-amber-900/10 dark:border-amber-900/40 dark:text-amber-300'
    : 'bg-[#F5F8FF] border-[#E0E9FA] text-[#6F85A6] dark:bg-blue-900/10 dark:border-blue-900/40 dark:text-blue-300'}`}>
    {children}
  </div>
);

/**
 * What the assessor concluded, and what the manager still has to chase.
 *
 * The follow-up list is derived from the answers by the API, not typed by
 * anyone — an assessment that says "swallowing concern: yes" should raise the
 * swallowing plan whether or not the assessor remembered to mention it.
 */
const RecommendationCard = ({ assessment, onOpen }) => (
  <div className="rounded-xl border border-[#DCE8F9] bg-[#F6F9FF] dark:bg-blue-900/10 dark:border-blue-900/40 p-4">
    <div className="text-[9.5px] font-bold tracking-wider text-blue-600 mb-1.5">ASSESSOR RECOMMENDATION</div>
    <h3 className="text-[16px] font-bold text-[#1E293B] dark:text-gray-100">{assessment.recommendation}</h3>
    <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-2 whitespace-pre-wrap">{assessment.recommendation_reason}</p>
    {assessment.conditions && (
      <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-2"><strong>Conditions:</strong> {assessment.conditions}</p>
    )}
    {assessment.summary && (
      <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-3 pt-3 border-t border-[#DCE8F9] dark:border-blue-900/40 whitespace-pre-wrap">
        <strong className="block text-[11px] text-[#7B8BA4] mb-1">ASSESSMENT SUMMARY</strong>
        {assessment.summary}
      </p>
    )}
    {assessment.follow_up && (
      <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-3 whitespace-pre-wrap">
        <strong className="block text-[11px] text-[#7B8BA4] mb-1">OUTSTANDING / FOLLOW-UP</strong>
        {assessment.follow_up}
      </p>
    )}
    {assessment.concerns?.length > 0 && (
      <div className="mt-3 pt-3 border-t border-[#DCE8F9] dark:border-blue-900/40">
        <strong className="block text-[11px] text-[#7B8BA4] mb-1.5">TO CHECK BEFORE CARE STARTS</strong>
        <ul className="list-disc ml-4 text-[12.5px] text-[#51657F] dark:text-gray-300 space-y-1">
          {assessment.concerns.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </div>
    )}
    <p className="text-[11px] text-[#94A3B8] mt-3">
      {assessment.assessor_name} · assessed {formatDate(assessment.assessed_date)} · returned {formatDateTime(assessment.submitted_at)}
    </p>
    <Button variant="secondary" className="mt-3" leftIcon={<FileText className="w-4 h-4" />} onClick={onOpen}>
      View completed assessment
    </Button>
  </div>
);

/**
 * One enquiry, from first contact to hand-off.
 *
 * Each status shows exactly one panel — the next thing to do. Sending the
 * assessment emails an outside assessor a one-time link; their returned
 * recommendation is reviewed here; a suitable decision leads to planning, and
 * saving the plan creates the ordinary Enquiry List row for the existing
 * Convert form. Nothing after the hand-off is changed by this page.
 */

const EnquiryDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({});
  const [showAssessment, setShowAssessment] = useState(false);

  const mode = data?.mode || 'carehome';
  const w = wording(mode);

  const load = async () => {
    setLoading(true);
    try {
      const response = await careEnquiryService.get(id);
      if (response.success) {
        setData(response.data);
        setForm(response.data.plan || {});
        setError('');
      } else {
        setError(response.message || 'Could not load the enquiry');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not load the enquiry'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  /** Run one action, then take the fresh record it returns. */
  const run = async (fn, successMessage) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await fn();
      if (response.success) {
        if (response.data) {
          setData(response.data);
          setForm(response.data.plan || {});
        }
        setNotice(response.message || successMessage || '');
      } else {
        setError(response.message || 'That could not be saved');
      }
    } catch (err) {
      setError(errorMessage(err, 'That could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const field = (name, label, { type = 'text', options, full = false, placeholder = '', required = false, rows = 3 } = {}) => (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className={labelCls} htmlFor={`f-${name}`}>
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      {type === 'select' ? (
        <select id={`f-${name}`} className={inputCls} value={form[name] || ''} onChange={(e) => set({ [name]: e.target.value })}>
          <option value="">Select an option</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea id={`f-${name}`} rows={rows} className={inputCls} placeholder={placeholder}
          value={form[name] || ''} onChange={(e) => set({ [name]: e.target.value })} />
      ) : (
        <input id={`f-${name}`} type={type} className={inputCls} placeholder={placeholder}
          value={form[name] || ''} onChange={(e) => set({ [name]: e.target.value })} />
      )}
    </div>
  );

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setNotice('Link copied.');
    } catch {
      setNotice('Select the link and copy it.');
    }
  };

  const printAssessment = () => {
    document.body.classList.add('printing-assessment');
    window.print();
    setTimeout(() => document.body.classList.remove('printing-assessment'), 500);
  };

  if (loading) return <p className="p-10 text-center text-[13px] text-[#94A3B8]">Loading…</p>;

  if (!data) {
    return (
      <div className="p-10 text-center">
        <AlertCircle className="w-7 h-7 mx-auto text-amber-500 mb-3" />
        <p className="text-[13.5px] text-[#64748B]">{error || 'This enquiry could not be found.'}</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/manager/enquiries-assessments')}>
          Back to enquiries
        </Button>
      </div>
    );
  }

  const { status, invite, assessment, review } = data;

  /* ── The one panel this status calls for ─────────────────────────────── */
  let panel = null;

  if (status === 'NEW') {
    panel = (
      <Panel
        kicker="STEP 1 OF 5"
        title="Ready to assess care needs"
        description="Keep the assessment connected to this enquiry."
        footer={<Button variant="primary" loading={busy} onClick={() => run(() => careEnquiryService.startAssessment(id))}>Start assessment</Button>}
      >
        <Note>
          The person&apos;s name and enquiry details are already linked. The assessment is the next step;
          this person is not yet an active {w.noun}.
        </Note>
        <Line label="Person">{data.first_name} {data.surname}</Line>
        <Line label="Care requested">{data.care_requested || 'To be assessed'}</Line>
        <Line label="Assessment">Not started</Line>
      </Panel>
    );
  }

  if (status === 'ASSESSMENT' && !invite?.link) {
    panel = (
      <Panel
        kicker="STEP 2 OF 5"
        title={w.home ? 'Send pre-admission assessment' : 'Send home-care assessment'}
        description="Invite the assessor to complete the form on a phone, tablet or computer."
        footer={(
          <Button
            variant="primary"
            loading={busy}
            onClick={() => run(() => careEnquiryService.sendInvite(id, {
              assessor_name: form.assessor_name, email: form.assessor_email, visit_at: form.visit_at,
            }))}
          >
            Send assessment
          </Button>
        )}
      >
        <Note>
          The assessor gives a recommendation. You review the returned assessment before deciding suitability.
        </Note>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field('assessor_name', 'Assessor name', { required: true })}
          {field('assessor_email', 'Assessor email', { type: 'email', required: true })}
          {field('visit_at', 'Visit date & time (optional)', { type: 'datetime-local' })}
        </div>

        {/* Exactly what the email will carry, so the manager can see a missing
            address or phone number BEFORE the assessor does. */}
        <div className="rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-[#F8FAFF] dark:bg-[#1f242b] p-4">
          <strong className="block text-[11px] font-bold tracking-wide text-[#7B8BA4] mb-3">
            ASSESSMENT VISIT DETAILS — INCLUDED IN THE EMAIL
          </strong>
          {[
            [w.home ? 'Admission reference' : 'Enquiry reference', data.reference],
            ['Person to assess', `${data.first_name} ${data.surname}`],
            ['Assessment address', data.address],
            ['Enquirer / contact', data.contact_name ? `${data.contact_name}${data.contact_relationship ? ` (${data.contact_relationship.toLowerCase()})` : ''}` : ''],
            ['Contact number', data.contact_phone],
            ['Care requested', data.care_requested],
            ['Visit date & time', form.visit_at ? formatDateTime(form.visit_at) : 'To be arranged with the contact'],
          ].map(([label, value]) => (
            <div key={label} className="flex flex-wrap justify-between gap-3 py-1.5 text-[12.5px]">
              <span className="text-[#8C9BB0]">{label}</span>
              <span className={`font-semibold text-right ${value ? 'text-[#334155] dark:text-gray-200' : 'text-amber-600 dark:text-amber-400'}`}>
                {value || 'Not recorded — the assessor will not see this'}
              </span>
            </div>
          ))}
        </div>

        <p className="text-[11.5px] text-[#94A3B8]">
          One link, one assessment. The sent and completed times are recorded here automatically.
        </p>
      </Panel>
    );
  }

  if (status === 'ASSESSMENT' && invite?.link) {
    const stateText = {
      RETURNED: 'Assessment returned',
      REPLACED: 'Invitation replaced',
      EXPIRED: 'Invitation expired',
      IN_PROGRESS: 'Assessment in progress',
      AWAITING: 'Awaiting assessor',
    }[invite.state] || 'Awaiting assessor';

    panel = (
      <Panel
        kicker="STEP 2 OF 5"
        title={w.home ? 'Pre-admission assessment' : 'Initial home-care assessment'}
        description="The assessor completes the linked form and returns their recommendation."
        footer={<Button variant="secondary" loading={busy} onClick={() => run(load)}>Check for returned assessment</Button>}
      >
        <Note tone={invite.email_sent ? 'blue' : 'amber'}>
          <strong>{stateText}</strong>
          <br />
          {invite.email_sent
            ? 'The assessor has been emailed their link.'
            : 'The email could not be sent. Copy the link below and share it with the assessor.'}
        </Note>
        <Line label="Assessor">{invite.assessor_name}</Line>
        <Line label="Email">{invite.email}</Line>
        <Line label="Visit date &amp; time">{invite.visit_at ? formatDateTime(invite.visit_at) : 'To be arranged with the contact'}</Line>
        <Line label="Sent">{formatDateTime(invite.sent_at)}</Line>
        <Line label="Started">{formatDateTime(invite.started_at)}</Line>
        <Line label="Link expires">{formatDateTime(invite.expires_at)}</Line>

        <div>
          <label className={labelCls} htmlFor="assessorLink">Assessor form link</label>
          <input id="assessorLink" readOnly className={inputCls} value={invite.link} onFocus={(e) => e.target.select()} />
          <div className="flex flex-wrap gap-3 mt-3">
            <a className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-[12.5px] font-semibold" href={invite.link} target="_blank" rel="noopener noreferrer">
              Open assessor form <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <Button variant="secondary" leftIcon={<Copy className="w-4 h-4" />} onClick={() => copyLink(invite.link)}>Copy link</Button>
          </div>
        </div>
      </Panel>
    );
  }

  if (status === 'REVIEW') {
    panel = (
      <Panel
        kicker="STEP 3 OF 5"
        title="Manager review"
        description="Record the suitability decision and its reasons."
        footer={(
          <Button
            variant="primary"
            loading={busy}
            onClick={() => run(() => careEnquiryService.review(id, {
              reviewer: form.reviewer, decision: form.decision, rationale: form.rationale,
            }))}
          >
            Save decision
          </Button>
        )}
      >
        {assessment && <RecommendationCard assessment={assessment} onOpen={() => setShowAssessment(true)} />}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field('reviewer', 'Reviewing manager', { required: true })}
          {field('decision', 'Assessment outcome', {
            type: 'select', required: true,
            options: ['Suitable', 'Deferred – more information needed', 'Declined'],
          })}
          {field('rationale', 'Decision rationale / next actions', { type: 'textarea', full: true, required: true })}
        </div>
      </Panel>
    );
  }

  if (status === 'DEFERRED' || status === 'DECLINED') {
    panel = (
      <Panel
        kicker="STEP 3 OF 5"
        title={status === 'DEFERRED' ? 'Assessment deferred' : 'Enquiry declined'}
        description={`This enquiry stays outside the active ${w.noun} list.`}
        footer={status === 'DEFERRED'
          ? <Button variant="primary" loading={busy} onClick={() => run(() => careEnquiryService.reopen(id))}>Reopen assessment</Button>
          : <Button variant="secondary" onClick={() => navigate('/manager/enquiries-assessments')}>Back to enquiries</Button>}
      >
        <Note tone="amber">{review?.rationale}</Note>
        <Line label="Reviewed by">{review?.reviewer || 'Not recorded'}</Line>
        <Line label="Decision">{review?.decision || 'Not recorded'}</Line>
        {assessment && (
          <Button variant="secondary" leftIcon={<FileText className="w-4 h-4" />} onClick={() => setShowAssessment(true)}>
            View completed assessment
          </Button>
        )}
      </Panel>
    );
  }

  if (status === 'SUITABLE') {
    panel = (
      <Panel
        kicker="STEP 4 OF 5"
        title={w.home ? 'Plan admission' : 'Plan the care package'}
        description="Suitability is recorded. Agree the practical arrangements next."
        footer={(
          <>
            <Button variant="secondary" loading={busy} onClick={() => run(() => careEnquiryService.savePlan(id, { ...form, submit: false }), 'Draft saved.')}>
              Save draft
            </Button>
            <Button variant="primary" loading={busy} onClick={() => run(() => careEnquiryService.savePlan(id, { ...form, submit: true }))}>
              {w.home ? 'Save admission plan' : 'Save care-package plan'}
            </Button>
          </>
        )}
      >
        <Note>
          <strong>Suitable {w.home ? 'for admission' : 'for home care'}</strong>
          <br />{review?.rationale}
        </Note>
        {/* The assessment has to stay open at THIS step, not just at review:
            the room, the floor and the equipment all follow from what the
            assessor found about mobility, falls and evacuation. */}
        {assessment && (
          <div className="flex flex-wrap items-center gap-3">
            {assessment.concerns?.length > 0 && (
              <span className="text-[12px] text-amber-700 dark:text-amber-400">
                {assessment.concerns.length} follow-up{assessment.concerns.length === 1 ? '' : 's'} to check before care starts
              </span>
            )}
            <Button variant="secondary" className="ml-auto" leftIcon={<FileText className="w-4 h-4" />} onClick={() => setShowAssessment(true)}>
              View completed assessment
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field('plannedDate', w.home ? 'Proposed admission date' : 'Proposed care start date', { type: 'date', required: true })}
          {w.home
            ? field('room', 'Room', { required: true })
            : field('coordinator', 'Care coordinator', { required: true })}
          {w.home && field('unit', 'Unit (optional)')}
          {w.home && field('floor', 'Floor', { type: 'select', options: ['Ground floor', 'First floor', 'Second floor', 'Other'] })}
          {field('planNotes', w.home ? 'Admission arrangements' : 'Agreed care package / visit arrangements', {
            type: 'textarea', full: true, required: true,
            placeholder: w.home
              ? 'Record transport, equipment, funding and agreed next steps.'
              : 'Record the agreed support, visit pattern, staffing and contingency arrangements.',
          })}
          <label className="sm:col-span-2 flex items-start gap-2.5 text-[12.5px] text-[#536681] dark:text-gray-400 leading-relaxed">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-blue-600"
              checked={form.planReady === true}
              onChange={(e) => set({ planReady: e.target.checked })}
            />
            <span>I confirm the required arrangements are ready.</span>
          </label>
        </div>
        <p className="text-[11.5px] text-[#94A3B8]">
          Saving the plan adds this person to the Enquiry List. They are not an active {w.noun} until the
          conversion form is completed.
        </p>
      </Panel>
    );
  }

  if (status === 'PLANNED') {
    panel = (
      <Panel
        kicker="STEP 4 OF 5"
        title={w.home ? 'Admission planned' : 'Care start planned'}
        description={`Ready to convert to a ${w.noun} in the Enquiry List.`}
        footer={(
          <Button
            variant="primary"
            onClick={() => navigate('/manager/enquiries/convert', { state: { selectedIds: [data.residence_id] } })}
          >
            Convert to {w.noun} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      >
        <Note>
          The conversion form will fill in the details that match this assessment. Anything it cannot
          match is left for you to complete.
        </Note>
        <Line label={w.home ? 'Planned admission date' : 'Planned care start date'}>{formatDate(data.plan?.plannedDate)}</Line>
        {w.home
          ? <Line label="Room">{data.plan?.room || 'Not recorded'}</Line>
          : <Line label="Care coordinator">{data.plan?.coordinator || 'Not recorded'}</Line>}
        <Line label="Arrangements">{data.plan?.planNotes || 'Not recorded'}</Line>
        {assessment && (
          <Button variant="secondary" leftIcon={<FileText className="w-4 h-4" />} onClick={() => setShowAssessment(true)}>
            View completed assessment
          </Button>
        )}
      </Panel>
    );
  }

  if (status === 'CONVERTED') {
    panel = (
      <Panel
        kicker="STEP 5 OF 5"
        title={`Active ${w.noun}`}
        description="The enquiry, assessment and decision stay attached to this record."
        footer={(
          <Button variant="primary" onClick={() => navigate(`/manager/residences/${data.residence_id}`)}>
            View {w.noun} profile <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      >
        <Line label={w.home ? 'Admission date' : 'Care start date'}>{formatDate(data.plan?.actualDate || data.plan?.plannedDate)}</Line>
        <Line label="Converted">{formatDateTime(data.converted_at)}</Line>
        <Line label="Manager decision">{review?.rationale || 'Not recorded'}</Line>
        {assessment && (
          <Button variant="secondary" leftIcon={<FileText className="w-4 h-4" />} onClick={() => setShowAssessment(true)}>
            View completed assessment
          </Button>
        )}
      </Panel>
    );
  }

  return (
    <div className="w-full space-y-6 pb-8">
      <button
        type="button"
        onClick={() => navigate('/manager/enquiries-assessments')}
        className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-blue-600 hover:text-blue-700"
      >
        <ArrowLeft className="w-4 h-4" /> Back to enquiries
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#E6EFFF] dark:bg-blue-900/20 text-[#2F62D3] dark:text-blue-300 grid place-items-center font-bold shrink-0">
          {initials(data)}
        </span>
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold text-[#1E293B] dark:text-gray-100 tracking-tight">{data.first_name} {data.surname}</h1>
          <p className="text-[12px] text-[#7B8BA4] mt-1">
            {data.reference} · {w.home ? 'Care home' : 'Domiciliary care'}
          </p>
        </div>
        <span className="ml-auto"><StatusBadge status={status} mode={mode} /></span>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-xl p-4 flex gap-3 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}
      {notice && (
        <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-900/40 rounded-xl p-4 flex gap-3 text-emerald-700 dark:text-emerald-400">
          <Check className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">{notice}</p>
        </div>
      )}

      <Journey status={status} mode={mode} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-5 items-start">
        {panel}

        <aside className={`${panelCls} p-5`}>
          <h3 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">Enquiry details</h3>
          <dl className="text-[12px]">
            {[
              ['Enquiry reference', data.reference],
              ['Contact', data.contact_name
                ? `${data.contact_name}${data.contact_relationship ? ` (${data.contact_relationship.toLowerCase()})` : ''}${data.contact_phone ? `\n${data.contact_phone}` : ''}`
                : 'Not recorded'],
              ['Referral source', data.referral_source || 'Not recorded'],
              ['Care requested', data.care_requested || 'To be assessed'],
              ['Address', data.address || 'Not recorded'],
              ['Reason for enquiry', data.reason || 'Not recorded'],
              ['Enquiry date', formatDate(data.enquiry_date)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-[10.5px] text-[#96A3B6] mt-4">{label}</dt>
                <dd className="text-[12px] text-[#51657F] dark:text-gray-300 mt-1.5 whitespace-pre-wrap break-words">{value}</dd>
              </div>
            ))}
          </dl>

          <div className="border-t border-[#E3E9F3] dark:border-gray-800 mt-5 pt-4">
            <h3 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100 mb-3">Progress</h3>
            {(data.history || []).map((h, i) => (
              <p key={i} className="text-[11px] text-[#8C9BB0] my-2.5">
                <b className="block text-[#617895] dark:text-gray-300 font-medium">{statusLabel(h.status, mode)}</b>
                {formatDateTime(h.at)}
                {h.note && <span className="block text-[10.5px] mt-0.5">{h.note}</span>}
              </p>
            ))}
          </div>
        </aside>
      </div>

      {/* The completed assessment, in full */}
      {assessment && (
        <Modal
          isOpen={showAssessment}
          onClose={() => setShowAssessment(false)}
          title="Returned assessment"
          maxWidth="max-w-4xl"
          footer={(
            <div className="flex justify-between gap-3 w-full">
              <Button variant="secondary" leftIcon={<Printer className="w-4 h-4" />} onClick={printAssessment}>
                Print / Save PDF
              </Button>
              <Button variant="secondary" onClick={() => setShowAssessment(false)}>Close</Button>
            </div>
          )}
        >
          <div id="assessmentPrintArea">
            <p className="text-[12px] text-[#7B8BA4] mb-4">
              {data.first_name} {data.surname} · {data.reference} · assessed by {assessment.assessor_name} on {formatDate(assessment.assessed_date)} · returned {formatDateTime(assessment.submitted_at)}
            </p>
            <div className="rounded-xl border border-[#DCE8F9] bg-[#F6F9FF] dark:bg-blue-900/10 dark:border-blue-900/40 p-4 mb-5">
              <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100">{assessment.recommendation}</h3>
              <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-2 whitespace-pre-wrap">{assessment.recommendation_reason}</p>
              {assessment.conditions && (
                <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-2"><strong>Conditions:</strong> {assessment.conditions}</p>
              )}
              {assessment.summary && (
                <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-3 whitespace-pre-wrap">
                  <strong>Summary:</strong> {assessment.summary}
                </p>
              )}
              {assessment.follow_up && (
                <p className="text-[12.5px] text-[#51657F] dark:text-gray-300 mt-2 whitespace-pre-wrap">
                  <strong>Outstanding / follow-up:</strong> {assessment.follow_up}
                </p>
              )}
            </div>
            {/* Section → group → answer, in the order the assessor worked
                through them. Questions left unanswered are omitted, so the
                manager reads findings rather than a wall of "Not recorded". */}
            {assessment.sections.map((section) => (
              <section key={section.id} className="border-t border-[#E3E9F3] dark:border-gray-800 pt-4 mt-4">
                <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 mb-1">{section.title}</h3>
                {section.groups.map((group) => (
                  <div key={group.title} className="mt-3">
                    <h4 className="text-[12.5px] font-semibold text-[#71839E] dark:text-gray-400">{group.title}</h4>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                      {group.answers.map((answer) => (
                        <div key={answer.label}>
                          <dt className="text-[11px] text-[#8A9AB0] mt-3">{answer.label}</dt>
                          <dd className="text-[12.5px] text-[#344B6D] dark:text-gray-300 mt-1 whitespace-pre-wrap">{answer.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </section>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EnquiryDetail;
